import { Button } from "@/components/flexkit/Button";
import { SearchInput } from "@/components/flexkit/SearchInput";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { WebhookCard } from "@/components/webhooks/WebhookCard";
import { useListWebhooks } from "@/queries/webhook-queries";
import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { formatError } from "@/utils/errors";
import { isTouchDevice } from "@/utils/is-touch-device";
import { matchSorter } from "match-sorter";
import { useEffect, useMemo, useRef, useState, type FC } from "react";

export const WebhookList: FC = () => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [searchQuery, setSearchQuery] = useState<string>("");

    const {
        data: webhooks,
        error: webhooksError,
        isPending: isPendingWebhooks,
        refetch: refetchWebhooks,
    } = useListWebhooks();

    useEffect(() => {
        // Auto-focus search on non-touch devices
        if (!isTouchDevice()) {
            inputRef.current?.focus();
        }
    }, []);

    const searchFilteredWebhooks = useMemo(() => {
        if (!webhooks) return [];

        if (!searchQuery.trim()) {
            return [...webhooks].sort((a, b) => {
                const nameA = (a.metadata?.name || "").trim().toLowerCase();
                const nameB = (b.metadata?.name || "").trim().toLowerCase();
                return nameA.localeCompare(nameB);
            });
        }

        return matchSorter(webhooks, searchQuery, {
            keys: ["metadata.name", "metadata.description", "spec.scope", "id"],
        });
    }, [webhooks, searchQuery]);

    const showErrorState = !searchQuery && !isPendingWebhooks && webhooksError && (!webhooks || webhooks.length === 0);
    const showEmptyState = !searchQuery && !isPendingWebhooks && !webhooksError && webhooks?.length === 0;

    if (showErrorState) {
        return (
            <EmptyStateCard
                title="Failed to load webhooks"
                description={webhooksError ? formatError(webhooksError) : "There was an error loading your webhooks."}
                data-testid="webhooks-error-state"
            >
                <Button
                    variant="secondary"
                    LeadingIcon={IconRefresh}
                    onClick={() => refetchWebhooks()}
                    data-tracking-id="retry-load-webhooks"
                >
                    Retry
                </Button>
            </EmptyStateCard>
        );
    }

    if (showEmptyState) {
        return (
            <EmptyStateCard
                title="No webhooks configured"
                description="Webhooks allow external services to trigger your automations. Create a webhook to get started."
                data-testid="webhooks-empty-state"
            />
        );
    }

    return (
        <div className="flex flex-col gap-3 @container" data-testid="webhooks-list">
            <div className="flex flex-col gap-2 @md:flex-row @md:items-center">
                <SearchInput
                    ref={inputRef}
                    placeholder="Search webhooks"
                    wrapperClassName="flex-1"
                    className="max-w-none"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClear={() => setSearchQuery("")}
                    data-testid="webhook-search-input"
                />
            </div>

            {isPendingWebhooks ? (
                <div className="flex flex-col gap-2">
                    <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-55" />
                    <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-35" />
                    <SkeletonBlock ready={false} animate={false} className="h-[122px] rounded-xl opacity-15" />
                </div>
            ) : searchFilteredWebhooks.length === 0 && searchQuery.trim() ? (
                <EmptyStateCard
                    title="No matching webhooks"
                    description={`No webhooks match "${searchQuery}". Try a different search term.`}
                    data-testid="no-matching-webhooks-empty-state"
                />
            ) : (
                <ul className="flex flex-col gap-2" data-testid="webhooks-list-items">
                    {searchFilteredWebhooks.map((webhook) => (
                        <li key={webhook.id}>
                            <WebhookCard webhook={webhook} />
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};
